import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ClubAvatarImage from '../ClubAvatarImage';

interface Club {
    id?: string;
    title: string;
    description: string;
    lat: any;
    lng: any;
    address1: any;
    streetNr?: string;
    zipCode?: string;
    city?: string;
    regionName: string;
    regionalUnitName: string;
    phone1?: string;
    phone2?: string;
    email?: string;
}

interface ListOfClubsProps {
    clubs: Club[];
    isLoading?: boolean;
    handleClick: (title: string, lat: any, lng: any, id?: string) => void;
}

const getClubId = (item: Club) => item.id || item.description.match(/[0-9a-fA-F-]{36}/)?.[0];

const normalizeText = (value: unknown) =>
    String(value || '')
        .trim()
        .toLowerCase();

const isUnknownLocationValue = (value: unknown) => normalizeText(value) === '<άγνωστο>' || normalizeText(value) === '<unknown>';

const getDisplayValue = (value: unknown) => {
    const displayValue = String(value || '').trim();
    return displayValue && !isUnknownLocationValue(displayValue) ? displayValue : '';
};

const getLocation = (item: Club) =>
    [
        [item.address1, item.streetNr].map(getDisplayValue).filter(Boolean).join(' '),
        [item.zipCode, item.city].map(getDisplayValue).filter(Boolean).join(' ')
    ]
        .filter(Boolean)
        .join(', ');

const ListOfClubs = ({ clubs, isLoading = false, handleClick }: ListOfClubsProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const handleOpenDetails = (item: Club) => {
        const clubId = getClubId(item);
        if (!clubId) return;

        navigate(`/public/clubs/manage/${clubId}/details`);
    };

    return (
        <Box
            sx={{
                height: '100%',
                overflowY: 'auto',
                background: '#fff',
                scrollbarWidth: 'thin',
                scrollbarColor: '#9aa8b6 #eef2f6',
                '&::-webkit-scrollbar': { width: 10 },
                '&::-webkit-scrollbar-track': { background: '#eef2f6' },
                '&::-webkit-scrollbar-thumb': {
                    background: '#9aa8b6',
                    borderRadius: 10,
                    border: '2px solid #eef2f6'
                }
            }}
        >
            {isLoading && (
                <Stack spacing={1.5} sx={{ p: 1.5 }}>
                    {[1, 2, 3].map((item) => (
                        <Skeleton key={item} variant="rounded" height={172} />
                    ))}
                </Stack>
            )}

            {!isLoading && clubs.length > 0
                ? clubs.map((item, index) => (
                      <Box
                          key={`${item.title}-${index}`}
                          onClick={() => handleOpenDetails(item)}
                          sx={{
                              display: 'grid',
                              gridTemplateColumns: '112px 1fr',
                              gap: 1.5,
                              p: 1.5,
                              borderBottom: '1px solid #e5eaf0',
                              cursor: 'pointer',
                              transition: 'background 160ms ease, box-shadow 160ms ease',
                              '&:hover': {
                                  background: '#f5faff',
                                  boxShadow: 'inset 3px 0 0 #1976d2'
                              }
                          }}
                      >
                          <ClubAvatarImage
                              clubId={item.id}
                              alt={item.title || t('UnnamedClub')}
                              iconSize={42}
                              sx={{
                                  height: 150,
                                  width: '100%',
                                  borderRadius: 1.5,
                                  border: '1px solid #dce4ec'
                              }}
                          />

                          <Stack spacing={0.75} sx={{ minWidth: 0, ml: 0.75 }}>
                              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                  <Typography sx={{ color: '#006ce4', fontWeight: 900, lineHeight: 1.25, fontSize: 16 }}>
                                      {item.title || t('UnnamedClub')}
                                  </Typography>
                              </Stack>

                              <Stack
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="flex-start"
                                  onClick={(event) => {
                                      event.stopPropagation();
                                      handleClick(item.title, item.lat, item.lng, item.id);
                                  }}
                                  sx={{
                                      color: '#4b5563',
                                      cursor: 'zoom-in',
                                      borderRadius: 1,
                                      '&:hover': {
                                          color: '#075aaa',
                                          background: '#eef6ff'
                                      }
                                  }}
                              >
                                  <LocationOnOutlinedIcon sx={{ fontSize: 17, mt: 0.2, flexShrink: 0 }} />
                                  <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
                                      {getLocation(item) || t('LocationNotAvailable')}
                                  </Typography>
                              </Stack>

                              {(item.phone1 || item.phone2) && (
                                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: '#4b5563' }}>
                                      <PhoneOutlinedIcon sx={{ fontSize: 16 }} />
                                      <Typography variant="body2">{item.phone1 || item.phone2}</Typography>
                                  </Stack>
                              )}

                              {item.email && (
                                  <Stack
                                      direction="row"
                                      spacing={0.5}
                                      alignItems="center"
                                      onClick={(event) => event.stopPropagation()}
                                      sx={{ minWidth: 0 }}
                                  >
                                      <EmailOutlinedIcon sx={{ fontSize: 16, color: '#4b5563' }} />
                                      <Typography
                                          component="a"
                                          href={`mailto:${item.email}`}
                                          variant="body2"
                                          sx={{
                                              color: '#075aaa',
                                              fontWeight: 700,
                                              textDecoration: 'none',
                                              overflowWrap: 'anywhere',
                                              '&:hover': { textDecoration: 'underline' }
                                          }}
                                      >
                                          {item.email}
                                      </Typography>
                                  </Stack>
                              )}

                          </Stack>
                      </Box>
                  ))
                : !isLoading && (
                      <Box sx={{ p: 3 }}>
                          <Typography sx={{ fontWeight: 800 }}>{t('NoClubsOrSchoolsFound', { defaultValue: 'No clubs found' })}</Typography>
                          <Typography variant="body2" color="text.secondary">
                              {t('TryAgainWhileMapDataLoads', { defaultValue: 'Try again in a moment while the map data loads.' })}
                          </Typography>
                      </Box>
                  )}
        </Box>
    );
};

export default ListOfClubs;
